import { useState } from 'react';
import api from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Link
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';

const TwoFactorLogin = ({ userId, onSuccess, onCancel }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleVerify = async () => {
    if (!token || (useBackupCode ? token.length !== 8 : token.length !== 6)) {
      setError(`Please enter a valid ${useBackupCode ? '8-character backup code' : '6-digit code'}`);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/2fa/validate', {
        userId,
        token
      });

      // Store token and redirect
      localStorage.setItem('token', response.data.token);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && token.length > 0) {
      handleVerify();
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: 'background.default', p: 2 }}
    >
      <Card elevation={3} sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              <SecurityIcon color="primary" fontSize="large" />
              <Typography variant="h5" component="h2">
                Two-Factor Authentication
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Alert severity="info">
              {useBackupCode
                ? 'Enter one of your 8-character backup codes'
                : 'Enter the 6-digit code from your authenticator app'
              }
            </Alert>

            <TextField
              label={useBackupCode ? 'Backup Code' : 'Authentication Code'}
              value={token}
              onChange={(e) => {
                const value = useBackupCode
                  ? e.target.value.toUpperCase().slice(0, 8)
                  : e.target.value.replace(/\D/g, '').slice(0, 6);
                setToken(value);
              }}
              onKeyPress={handleKeyPress}
              placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
              fullWidth
              autoFocus
              inputProps={{
                maxLength: useBackupCode ? 8 : 6,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: useBackupCode ? '0.3rem' : '0.5rem',
                  fontFamily: useBackupCode ? 'monospace' : 'inherit'
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={loading || token.length === 0}
              fullWidth
              size="large"
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>

            <Box textAlign="center">
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setToken('');
                  setError('');
                }}
                sx={{ cursor: 'pointer' }}
              >
                {useBackupCode ? 'Use authenticator app code' : 'Use backup code instead'}
              </Link>
            </Box>

            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                fullWidth
              >
                Cancel
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TwoFactorLogin;
